import { useState, type DragEvent } from 'react'

type Drag = { from: number; to: number }

const SORTABLE_DRAG_TYPE = 'application/x-sortable-index'

export function previewOrder(count: number, drag: Drag | null): number[] {
  const order = Array.from({ length: count }, (_, index) => index)
  if (drag) {
    order.splice(drag.from, 1)
    order.splice(drag.to, 0, drag.from)
  }
  return order
}

export function useSortable(count: number, onMove: (from: number, to: number) => void) {
  const [started, setDrag] = useState<Drag & { count: number } | null>(null)
  const drag = started?.count === count ? started : null
  if (started && !drag) setDrag(null)

  return {
    order: previewOrder(count, drag),
    isDragged: (index: number) => drag?.from === index,
    listProps: {
      onDragOver: (event: DragEvent) => { if (drag) event.preventDefault() },
      onDrop: (event: DragEvent) => {
        event.preventDefault()
        if (drag && drag.from !== drag.to) onMove(drag.from, drag.to)
        setDrag(null)
      },
    },
    itemProps: (index: number, position: number, enabled = true) => ({
      draggable: enabled,
      onDragStart: (event: DragEvent<HTMLElement>) => {
        event.dataTransfer.effectAllowed = 'move'
        event.dataTransfer.setData(SORTABLE_DRAG_TYPE, String(index))
        setDragImage(event)
        // Reordering the source node inside dragstart cancels the drag in Chromium.
        setTimeout(() => setDrag({ from: index, to: index, count }))
      },
      onDragOver: (event: DragEvent) => {
        if (!drag) return
        event.preventDefault()
        if (index !== drag.from && position !== drag.to) setDrag({ ...drag, to: position })
      },
      onDragEnd: () => setDrag(null),
    }),
  }
}

// Select keeps a fixed-position hidden input inside the row, which stretches the
// browser's default drag image to the top-left of the page.
function setDragImage(event: DragEvent<HTMLElement>) {
  const source = event.currentTarget
  const bounds = source.getBoundingClientRect()
  const image = source.cloneNode(true) as HTMLElement
  for (const input of image.querySelectorAll('input[aria-hidden="true"]')) input.remove()
  Object.assign(image.style, { position: 'absolute', top: '-10000px', left: '0', width: `${bounds.width}px` })
  document.body.append(image)
  event.dataTransfer.setDragImage(image, event.clientX - bounds.left, event.clientY - bounds.top)
  setTimeout(() => image.remove())
}

export const DRAGGED_ITEM = 'border-primary/60 bg-primary/5 border-dashed [&>*]:opacity-40'
